import { AlertTriangle, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";

interface Props {
  title?: string;
  error?: Error | string | null;
  onRetry?: () => void;
  /** Show full-screen variant cho ErrorBoundary fallback. */
  fullScreen?: boolean;
}

/**
 * ErrorState — friendly inline/fullscreen error display với retry.
 *
 * Doc 31. Dùng bởi ErrorBoundary fallback hoặc trực tiếp trong page khi
 * data fetch fail.
 */
export function ErrorState({
  title = "Có lỗi xảy ra",
  error,
  onRetry,
  fullScreen = false,
}: Props): JSX.Element {
  const message =
    error == null
      ? "Vui lòng thử lại hoặc liên hệ admin nếu lỗi tiếp tục."
      : typeof error === "string"
        ? error
        : error.message ?? String(error);

  const body = (
    <div className="flex flex-col items-center text-center max-w-md mx-auto">
      <div className="mb-4 size-16 rounded-2xl bg-destructive/10 flex items-center justify-center text-destructive">
        <AlertTriangle className="size-8" />
      </div>
      <h3 className="text-base font-semibold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4 break-words">
        {message}
      </p>
      {onRetry ? (
        <Button onClick={onRetry} variant="outline">
          <RefreshCw className="size-4 mr-2" /> Thử lại
        </Button>
      ) : null}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-mesh p-4">
        {body}
      </div>
    );
  }
  return <div className="py-12">{body}</div>;
}
